import { useState, useMemo, useEffect, useRef } from "preact/hooks"
import { ChevronRight, ChevronDown, Minus, ArrowUpDown, ArrowUp, ArrowDown, PieChart, CalendarRange, Download, Loader2 } from "lucide-react"
import { format, parseISO, startOfMonth, endOfMonth } from "date-fns"
import { Link } from "wouter-preact"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { downloadHierarchyExport } from "../api/distributor_sales_api"
import { toast } from "../../../components/ui/use-toast.ts"
import {
  HierarchyNode,
  HierarchyResponse,
  SalesMetrics,
  DistributorSource,
  DistributorTableRow,
  RetailerProfitabilityRow,
  DistributorSalesReport,
} from "../types/distributor_sales"

type SortField = 'name' | 'fill_rate' | 'ordered_vendor_cost' | 'shipped_vendor_cost' |
                 'ordered_list_wholesale' | 'shipped_list_wholesale' |
                 'ordered_quantity' | 'shipped_quantity' | 'stores_ordered' | 'sku_count' |
                 'sales_dollars' | 'chargeback_dollars' | 'chargeback_pct' | 'invoice_count' |
                 'total_deductions' | 'selling_cost' | 'net' | 'deduction_rate_pct' | 'margin_pct'
type SortDirection = 'asc' | 'desc'

interface DistributorSalesTableProps {
  data: HierarchyResponse | null
  expandedNodes: Set<string>
  onToggleExpansion: (nodeId: string) => void
  onExpandAll: () => void
  onCollapseAll: () => void
  onOpenHierarchyPie?: (node: HierarchyNode) => void
  profitabilityData?: RetailerProfitabilityRow[]
  /** Selected table report — used to show the date range we use when matching deductions (profitability). */
  deductionWindowReport?: DistributorSalesReport | null
  /** Called when user edits per-row COGS override (dollars); null clears override for that retailer key. */
  on_selling_cost_override_change?: (retailer: string, dollars: number | null) => void
  show_deduction_data: boolean
  on_show_deduction_data_change: (show: boolean) => void
  /** When set, shows retailer mappings button beside deduction toggle while deduction data is visible. */
  retailer_mappings_report_id?: string | null
  /** Report backing the XLSX download; the export always covers every level, not just the expanded rows. */
  export_report_id?: string | null
  export_source?: DistributorSource
  className?: string
}

/** Deduction rate thresholds (deductions ÷ shipped vendor cost) — used without COGS margin context. */
export function deduction_rate_color_class(rate_pct: number): string {
  if (rate_pct < 30) return "text-green-600"
  if (rate_pct <= 35) return "text-yellow-600"
  if (rate_pct <= 40) return "text-orange-600"
  return "text-red-600"
}

/** Margin after COGS thresholds (net ÷ shipped vendor cost). */
export function margin_after_cogs_color_class(margin_pct: number): string {
  if (margin_pct < 10) return "text-red-600"
  if (margin_pct <= 20) return "text-yellow-600"
  return "text-green-600"
}

/** Mirrors server `report_deduction_invoice_date_range` for display labels. */
function format_deduction_invoice_window(report: DistributorSalesReport): string {
  if (report.start_date && report.end_date) {
    let s = parseISO(report.start_date)
    let e = parseISO(report.end_date)
    if (s.getTime() > e.getTime()) {
      const t = s
      s = e
      e = t
    }
    return `${format(s, "MMM d, yyyy")} – ${format(e, "MMM d, yyyy")}`
  }
  if (report.start_date) {
    return format(parseISO(report.start_date), "MMM d, yyyy")
  }
  if (report.end_date) {
    return format(parseISO(report.end_date), "MMM d, yyyy")
  }
  const d = parseISO(report.report_date)
  return `${format(startOfMonth(d), "MMM d, yyyy")} – ${format(endOfMonth(d), "MMM d, yyyy")}`
}

// Flatten hierarchy into table rows based on expansion state
function flattenHierarchy(
  nodes: HierarchyNode[],
  expandedNodes: Set<string>,
  depth: number = 0,
  parentId?: string
): DistributorTableRow[] {
  const rows: DistributorTableRow[] = []
  
  for (const node of nodes) {
    const hasChildren = node.children && node.children.length > 0
    const isExpanded = expandedNodes.has(node.id)
    
    rows.push({
      id: node.id,
      name: node.name,
      level: node.level,
      depth,
      metrics: node.metrics,
      has_children: hasChildren,
      is_expanded: isExpanded,
      parent_id: parentId,
    })
    
    // Add children if expanded
    if (hasChildren && isExpanded) {
      rows.push(...flattenHierarchy(node.children, expandedNodes, depth + 1, node.id))
    }
  }
  
  return rows
}

function findHierarchyNode(nodes: HierarchyNode[], id: string): HierarchyNode | null {
  for (const n of nodes) {
    if (n.id === id) return n
    if (n.children?.length) {
      const found = findHierarchyNode(n.children, id)
      if (found) return found
    }
  }
  return null
}

// Get all node IDs for expand all functionality
function getAllNodeIds(nodes: HierarchyNode[]): string[] {
  const ids: string[] = []
  for (const node of nodes) {
    ids.push(node.id)
    if (node.children && node.children.length > 0) {
      ids.push(...getAllNodeIds(node.children))
    }
  }
  return ids
}

function toNumber(value: number | string | undefined | null): number | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    return isNaN(parsed) ? undefined : parsed
  }
  return value
}

function formatCurrency(rawValue: number | string | undefined): string {
  const value = toNumber(rawValue)
  if (value === undefined) return "-"
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`
  }
  return `$${value.toFixed(2)}`
}

function formatNumber(rawValue: number | string | undefined): string {
  const value = toNumber(rawValue)
  if (value === undefined) return "-"
  return value.toLocaleString()
}

function formatPercent(rawValue: number | string | undefined): string {
  const value = toNumber(rawValue)
  if (value === undefined) return "-"
  return `${value.toFixed(1)}%`
}

function parse_dollar_input(raw: string): number | null {
  const t = raw.trim().replace(/[$,]/g, "")
  if (!t) return null
  const n = parseFloat(t)
  return Number.isFinite(n) ? n : null
}

interface CogsCellProps {
  retailer: string
  selling_cost: number | null | undefined
  on_save?: (retailer: string, dollars: number | null) => void
}

function CogsCell({ retailer, selling_cost, on_save }: CogsCellProps) {
  const [editing, set_editing] = useState(false)
  const [local_value, set_local_value] = useState("")
  const last_prop = useRef(selling_cost)

  if (selling_cost !== last_prop.current) {
    last_prop.current = selling_cost
    if (!editing) {
      set_local_value(selling_cost != null ? String(selling_cost) : "")
    }
  }

  if (!on_save) {
    return selling_cost != null ? (
      <span class="text-gray-800">{formatCurrency(selling_cost)}</span>
    ) : (
      <span class="text-gray-300">—</span>
    )
  }

  if (editing) {
    return (
      <Input
        type="text"
        inputMode="decimal"
        class="h-7 w-24 ml-auto text-right text-sm"
        value={local_value}
        onInput={(e: Event) => set_local_value((e.target as HTMLInputElement).value)}
        onBlur={() => {
          set_editing(false)
          const parsed = parse_dollar_input(local_value)
          on_save(retailer, parsed)
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur()
          }
        }}
      />
    )
  }

  return (
    <button
      type="button"
      class="text-gray-800 hover:text-blue-600 hover:underline"
      onClick={() => {
        set_local_value(selling_cost != null ? String(selling_cost) : "")
        set_editing(true)
      }}
    >
      {selling_cost != null ? formatCurrency(selling_cost) : "—"}
    </button>
  )
}

// Visual bar for relative comparison
function MetricBar({ value, maxValue, color }: { value: number | string; maxValue: number; color: string }) {
  const numValue = toNumber(value) || 0
  const width = maxValue > 0 ? Math.min((numValue / maxValue) * 100, 100) : 0
  
  return (
    <div class="flex items-center gap-2">
      <div class="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div 
          class="h-full rounded-full transition-all" 
          style={{ width: `${width}%`, backgroundColor: color }}
        />
      </div>
    </div>
  )
}

export function DistributorSalesTable({
  data,
  expandedNodes,
  onToggleExpansion,
  onExpandAll,
  onCollapseAll,
  onOpenHierarchyPie,
  profitabilityData = [],
  deductionWindowReport = null,
  on_selling_cost_override_change,
  show_deduction_data,
  on_show_deduction_data_change,
  retailer_mappings_report_id = null,
  export_report_id = null,
  export_source,
  className,
}: DistributorSalesTableProps) {
  // Flatten hierarchy based on expansion state
  const rows = useMemo(() => {
    if (!data?.nodes) return []
    return flattenHierarchy(data.nodes, expandedNodes)
  }, [data, expandedNodes])

  // Calculate max values for bars
  const maxValues = useMemo(() => {
    let maxSales = 0
    let maxChargebacks = 0
    let maxOrdered = 0
    
    rows.forEach((row: DistributorTableRow) => {
      const sales = toNumber(row.metrics.sales_dollars) || 0
      const chargebacks = toNumber(row.metrics.chargeback_dollars) || 0
      const ordered = toNumber(row.metrics.ordered) || 0
      
      if (sales > maxSales) maxSales = sales
      if (chargebacks > maxChargebacks) maxChargebacks = chargebacks
      if (ordered > maxOrdered) maxOrdered = ordered
    })
    
    return { sales: maxSales, chargebacks: maxChargebacks, ordered: maxOrdered }
  }, [rows])

  // Build lookup: retailer name -> profitability row (case-insensitive)
  const profitabilityByRetailer = useMemo(() => {
    const map = new Map<string, RetailerProfitabilityRow>()
    profitabilityData.forEach((r: RetailerProfitabilityRow) => {
      map.set(r.retailer.toLowerCase(), r)
    })
    return map
  }, [profitabilityData])

  function getProfitability(rowName: string): RetailerProfitabilityRow | undefined {
    return profitabilityByRetailer.get(rowName.toLowerCase())
  }

  const hasProfitabilityData = profitabilityData.length > 0

  // Determine column set based on what metrics are available
  // KeHE Chargebacks has chargeback_dollars, while Vendor Sales/UNFI has fill_rate
  const hasChargebackData = useMemo(() => {
    return rows.some((row: DistributorTableRow) => {
      const val = toNumber(row.metrics.chargeback_dollars)
      return val !== undefined && val !== 0
    })
  }, [rows])
  
  const hasFillRateData = useMemo(() => {
    return rows.some((row: DistributorTableRow) => {
      const val = toNumber(row.metrics.fill_rate)
      return val !== undefined && val !== 0
    })
  }, [rows])
  
  // Check which valuation columns are available
  const hasVendorCost = useMemo(() => {
    return rows.some((row: DistributorTableRow) => {
      return toNumber(row.metrics.ordered_vendor_cost) !== undefined || 
             toNumber(row.metrics.shipped_vendor_cost) !== undefined
    })
  }, [rows])

  const hasListWholesale = useMemo(() => {
    return rows.some((row: DistributorTableRow) => {
      return toNumber(row.metrics.ordered_list_wholesale) !== undefined ||
             toNumber(row.metrics.shipped_list_wholesale) !== undefined
    })
  }, [rows])
  
  const hasQuantity = useMemo(() => {
    return rows.some((row: DistributorTableRow) => {
      return row.metrics.ordered_quantity !== undefined || 
             row.metrics.shipped_quantity !== undefined
    })
  }, [rows])
  
  // Use chargeback columns for KeHE chargebacks, fill rate columns for everything else
  const useChargebackColumns = hasChargebackData && !hasFillRateData

  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>("shipped_vendor_cost")
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const [downloading, setDownloading] = useState(false)

  async function handleDownload() {
    setDownloading(true)
    try {
      await downloadHierarchyExport({
        report_id: export_report_id ?? undefined,
        source: export_source,
        // The file carries the deduction columns whenever we have them, toggle or not.
        include_deductions: hasProfitabilityData,
      })
    } catch (error) {
      console.error("Failed to export sales by retailer:", error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to download the spreadsheet",
      })
    } finally {
      setDownloading(false)
    }
  }

  function handleSort(field: SortField) {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }
  
  // Helper to get sort value from a row
  function getSortValue(row: DistributorTableRow, field: SortField): number | string {
    if (field === 'name') return row.name
    if (
      field === 'total_deductions' ||
      field === 'selling_cost' ||
      field === 'net' ||
      field === 'deduction_rate_pct' ||
      field === 'margin_pct'
    ) {
      const p = getProfitability(row.name)
      if (!p) return 0
      if (field === 'total_deductions') return p.total_deductions
      if (field === 'selling_cost') return p.selling_cost ?? 0
      if (field === 'net') return p.net ?? 0
      if (field === 'margin_pct') return p.margin_pct ?? 0
      return p.deduction_rate_pct ?? 0
    }
    return toNumber((row.metrics as any)[field]) || 0
  }
  
  // Compare function for sorting
  function compareRows(a: DistributorTableRow, b: DistributorTableRow, field: SortField, direction: SortDirection): number {
    const aVal = getSortValue(a, field)
    const bVal = getSortValue(b, field)
    
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction === 'asc' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal)
    }
    
    return direction === 'asc' 
      ? (aVal as number) - (bVal as number)
      : (bVal as number) - (aVal as number)
  }
  
  // Sort rows while preserving hierarchy structure
  // We sort children within their parent groups, not across the entire list
  const sortedRows = useMemo(() => {
    if (!sortField) return rows
    
    // Build a tree structure from the flat rows, sort each level, then flatten back
    function sortHierarchically(items: DistributorTableRow[]): DistributorTableRow[] {
      const result: DistributorTableRow[] = []
      
      // Get top-level rows (depth 0)
      const topLevel = items.filter((r: DistributorTableRow) => r.depth === 0)
      const sorted = [...topLevel].sort((a: DistributorTableRow, b: DistributorTableRow) => 
        compareRows(a, b, sortField!, sortDirection)
      )
      
      for (const row of sorted) {
        result.push(row)
        // If expanded, recursively add sorted children
        if (row.is_expanded) {
          const children = getChildrenRecursive(items, row.id, row.depth + 1)
          result.push(...children)
        }
      }
      
      return result
    }
    
    // Get all children of a parent, sorted, with their nested children
    function getChildrenRecursive(items: DistributorTableRow[], parentId: string, expectedDepth: number): DistributorTableRow[] {
      const result: DistributorTableRow[] = []
      
      // Find direct children (items at expectedDepth that come after parent in original order)
      const children = items.filter((r: DistributorTableRow) => 
        r.parent_id === parentId && r.depth === expectedDepth
      )
      
      const sortedChildren = [...children].sort((a: DistributorTableRow, b: DistributorTableRow) => 
        compareRows(a, b, sortField!, sortDirection)
      )
      
      for (const child of sortedChildren) {
        result.push(child)
        if (child.is_expanded) {
          const grandchildren = getChildrenRecursive(items, child.id, child.depth + 1)
          result.push(...grandchildren)
        }
      }
      
      return result
    }
    
    return sortHierarchically(rows)
  }, [rows, sortField, sortDirection])

  // Sort icon component
  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />
  }

  if (!data || rows.length === 0) {
    return (
      <div class={`text-center text-gray-500 py-8 ${className || ""}`}>
        <p>No hierarchy data available. Upload a report to see the data table.</p>
      </div>
    )
  }

  return (
    <div class={`bg-white border border-gray-200 rounded-lg overflow-hidden ${className || ""}`}>
      {/* Header Controls */}
      <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 class="text-lg font-semibold">
          {useChargebackColumns ? "Chargebacks by Chain" : "Sales by Retailer"}
        </h3>
        <div class="flex flex-wrap items-center gap-2 sm:gap-3">
          {show_deduction_data && deductionWindowReport ? (
            <span class="text-sm text-gray-600 max-w-md">
              <span class="font-medium text-gray-700">Dates used to match deductions: </span>
              {format_deduction_invoice_window(deductionWindowReport)}
            </span>
          ) : null}
          {show_deduction_data && retailer_mappings_report_id ? (
            <Button variant="outline" size="sm" class="shrink-0" asChild>
              <Link
                href={`/sales-analysis/retailer-mappings?report_id=${encodeURIComponent(retailer_mappings_report_id)}`}
              >
                Edit retailer name mappings
              </Link>
            </Button>
          ) : null}
          <Button
            type="button"
            variant={show_deduction_data ? "secondary" : "outline"}
            // @ts-ignore
            size="sm"
            class="shrink-0"
            disabled={!hasProfitabilityData}
            aria-pressed={show_deduction_data}
            title={
              hasProfitabilityData
                ? "Show COGS, deductions, net, and margin columns (based on shipped vendor cost)."
                : "No profitability data for this report."
            }
            onClick={() => {
              if (hasProfitabilityData) on_show_deduction_data_change(!show_deduction_data)
            }}
          >
            <CalendarRange className="h-4 w-4 mr-1.5 inline-block align-text-bottom" />
            {show_deduction_data ? "Hide deduction data" : "Show deduction data"}
          </Button>
          <Button
            type="button"
            variant="outline"
            // @ts-ignore
            size="sm"
            class="shrink-0"
            disabled={downloading}
            title="Download every level — chain, region, district, store and SKU — as an Excel file."
            onClick={handleDownload}
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 mr-1.5 inline-block align-text-bottom animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1.5 inline-block align-text-bottom" />
            )}
            {downloading ? "Preparing…" : "Download Excel"}
          </Button>
          <Button variant="outline" onClick={onCollapseAll}>
            Collapse All
          </Button>
        </div>
      </div>

      {/* Table */}
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-gray-50 border-b border-gray-200">
              <th 
                class="text-left py-3 px-4 font-medium text-gray-700 w-80 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('name')}
              >
                <div class="flex items-center">
                  {useChargebackColumns ? "Chain" : "Retailer / Store / Item"}
                  <SortIcon field="name" />
                </div>
              </th>
              {useChargebackColumns ? (
                <>
                  <th 
                    class="text-right py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('sales_dollars')}
                  >
                    <div class="flex items-center justify-end">Sales $<SortIcon field="sales_dollars" /></div>
                  </th>
                  <th 
                    class="text-right py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('chargeback_dollars')}
                  >
                    <div class="flex items-center justify-end">Chargeback $<SortIcon field="chargeback_dollars" /></div>
                  </th>
                  <th 
                    class="text-right py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('chargeback_pct')}
                  >
                    <div class="flex items-center justify-end">Chargeback %<SortIcon field="chargeback_pct" /></div>
                  </th>
                  <th 
                    class="text-right py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('invoice_count')}
                  >
                    <div class="flex items-center justify-end">Invoices<SortIcon field="invoice_count" /></div>
                  </th>
                </>
              ) : (
                <>
                  <th 
                    class="text-right py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('fill_rate')}
                  >
                    <div class="flex items-center justify-end">Fill Rate<SortIcon field="fill_rate" /></div>
                  </th>
                  {hasQuantity && (
                    <>
                      <th 
                        class="text-right py-3 px-4 font-medium text-gray-700 text-xs cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('ordered_quantity')}
                      >
                        <div class="flex items-center justify-end">Ordered (Qty)<SortIcon field="ordered_quantity" /></div>
                      </th>
                      <th 
                        class="text-right py-3 px-4 font-medium text-gray-700 text-xs cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('shipped_quantity')}
                      >
                        <div class="flex items-center justify-end">Shipped (Qty)<SortIcon field="shipped_quantity" /></div>
                      </th>
                    </>
                  )}
                  <th 
                    class="text-right py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('stores_ordered')}
                  >
                    <div class="flex items-center justify-end">Stores<SortIcon field="stores_ordered" /></div>
                  </th>
                  <th 
                    class="text-right py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('sku_count')}
                  >
                    <div class="flex items-center justify-end">SKUs<SortIcon field="sku_count" /></div>
                  </th>
                  {hasListWholesale && (
                    <>
                      <th 
                        class="text-right py-3 px-4 font-medium text-gray-700 text-xs cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('ordered_list_wholesale')}
                      >
                        <div class="flex items-center justify-end">Ordered (list wholesale)<SortIcon field="ordered_list_wholesale" /></div>
                      </th>
                      <th 
                        class="text-right py-3 px-4 font-medium text-gray-700 text-xs cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('shipped_list_wholesale')}
                      >
                        <div class="flex items-center justify-end">Shipped (list wholesale)<SortIcon field="shipped_list_wholesale" /></div>
                      </th>
                    </>
                  )}
                  {hasVendorCost && (
                    <>
                      <th 
                        class="text-right py-3 px-4 font-medium text-gray-700 text-xs cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('ordered_vendor_cost')}
                      >
                        <div class="flex items-center justify-end">Ordered (vendor cost)<SortIcon field="ordered_vendor_cost" /></div>
                      </th>
                      <th 
                        class="text-right py-3 px-4 font-medium text-gray-700 text-xs cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('shipped_vendor_cost')}
                      >
                        <div class="flex items-center justify-end">Shipped (vendor cost)<SortIcon field="shipped_vendor_cost" /></div>
                      </th>
                    </>
                  )}
                  {!hasVendorCost && !hasListWholesale && !hasQuantity && (
                    <>
                      <th class="text-right py-3 px-4 font-medium text-gray-700">Ordered</th>
                      <th class="text-right py-3 px-4 font-medium text-gray-700">Shipped</th>
                    </>
                  )}
                </>
              )}
              {/* Profitability columns — optional; toggled via Show deduction data */}
              {hasProfitabilityData && show_deduction_data && (
                <>
                  <th
                    class="text-right py-3 px-4 font-medium text-gray-700 border-l border-gray-200 bg-orange-50 cursor-pointer hover:bg-orange-100"
                    onClick={() => handleSort("total_deductions")}
                  >
                    <div class="flex items-center justify-end gap-1">
                      Deductions<SortIcon field="total_deductions" />
                    </div>
                  </th>
                  <th
                    class="text-right py-3 px-4 font-medium text-gray-700 bg-orange-50 cursor-pointer hover:bg-orange-100"
                    onClick={() => handleSort("selling_cost")}
                  >
                    <div class="flex items-center justify-end gap-1">
                      COGS<SortIcon field="selling_cost" />
                    </div>
                  </th>
                  <th class="text-right py-3 px-4 font-medium text-gray-700 bg-orange-50 cursor-pointer hover:bg-orange-100" onClick={() => handleSort('net')}>
                    <div class="flex items-center justify-end gap-1">Net<SortIcon field="net" /></div>
                  </th>
                  <th class="text-right py-3 px-4 font-medium text-gray-700 bg-orange-50 cursor-pointer hover:bg-orange-100" onClick={() => handleSort('deduction_rate_pct')}>
                    <div class="flex items-center justify-end gap-1">Ded. Rate<SortIcon field="deduction_rate_pct" /></div>
                  </th>
                  <th class="text-right py-3 px-4 font-medium text-gray-700 bg-orange-50 cursor-pointer hover:bg-orange-100" onClick={() => handleSort('margin_pct')}>
                    <div class="flex items-center justify-end gap-1">Margin<SortIcon field="margin_pct" /></div>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row: DistributorTableRow) => (
              <tr 
                key={row.id} 
                class="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                {/* Name column with expand/collapse */}
                <td class="py-2 px-4">
                  <div
                    class="flex items-center gap-1 min-w-0"
                    style={{ paddingLeft: `${row.depth * 24}px` }}
                  >
                    {/* Fixed slot so pie column aligns across rows */}
                    {!useChargebackColumns && onOpenHierarchyPie && row.has_children && row.level < 5 ? (
                      <button
                        type="button"
                        onClick={(e: MouseEvent) => {
                          e.stopPropagation()
                          const node = findHierarchyNode(data?.nodes ?? [], row.id)
                            ?? { id: row.id, name: row.name, level: row.level, metrics: row.metrics, children: [] }
                          onOpenHierarchyPie(node)
                        }}
                        class="w-7 h-7 shrink-0 flex items-center justify-center rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="View breakdown chart"
                      >
                        <PieChart className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <span class="w-7 h-7 shrink-0" aria-hidden="true" />
                    )}
                    {row.has_children ? (
                      <button
                        type="button"
                        onClick={() => onToggleExpansion(row.id)}
                        class="p-1 rounded hover:bg-gray-200 transition-colors shrink-0"
                      >
                        {row.is_expanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                      </button>
                    ) : (
                      <span class="w-6 flex justify-center shrink-0">
                        <Minus className="h-3 w-3 text-gray-300" />
                      </span>
                    )}
                    <span class={`min-w-0 truncate ${row.depth === 0 ? "font-semibold" : ""} ${row.depth > 2 ? "text-sm text-gray-600" : ""}`}>
                      {row.name}
                    </span>
                  </div>
                </td>
                
                {/* Metric columns */}
                {useChargebackColumns ? (
                  <>
                    <td class="py-2 px-4 text-right">
                      <div class="flex items-center justify-end gap-2">
                        <MetricBar 
                          value={row.metrics.sales_dollars || 0} 
                          maxValue={maxValues.sales} 
                          color="#16a34a" 
                        />
                        <span class="w-24 text-right">{formatCurrency(row.metrics.sales_dollars)}</span>
                      </div>
                    </td>
                    <td class="py-2 px-4 text-right">
                      <div class="flex items-center justify-end gap-2">
                        <MetricBar 
                          value={row.metrics.chargeback_dollars || 0} 
                          maxValue={maxValues.chargebacks} 
                          color="#dc2626" 
                        />
                        <span class="w-24 text-right">{formatCurrency(row.metrics.chargeback_dollars)}</span>
                      </div>
                    </td>
                    <td class="py-2 px-4 text-right">
                      {formatPercent(row.metrics.chargeback_pct)}
                    </td>
                    <td class="py-2 px-4 text-right">
                      {formatNumber(row.metrics.invoice_count)}
                    </td>
                  </>
                ) : (
                  <>
                    <td class="py-2 px-4 text-right">
                      <span class={`font-medium ${
                        (toNumber(row.metrics.fill_rate) || 0) >= 95 ? "text-green-600" :
                        (toNumber(row.metrics.fill_rate) || 0) >= 80 ? "text-yellow-600" :
                        "text-red-600"
                      }`}>
                        {formatPercent(row.metrics.fill_rate)}
                      </span>
                    </td>
                    {hasQuantity && (
                      <>
                        <td class="py-2 px-4 text-right text-sm">
                          {formatNumber(row.metrics.ordered_quantity)}
                        </td>
                        <td class="py-2 px-4 text-right text-sm">
                          {formatNumber(row.metrics.shipped_quantity)}
                        </td>
                      </>
                    )}
                    <td class="py-2 px-4 text-right">
                      {formatNumber(row.metrics.stores_ordered)}
                    </td>
                    <td class="py-2 px-4 text-right">
                      {formatNumber(row.metrics.sku_count)}
                    </td>
                    {hasListWholesale && (
                      <>
                        <td class="py-2 px-4 text-right text-sm">
                          {formatCurrency(row.metrics.ordered_list_wholesale)}
                        </td>
                        <td class="py-2 px-4 text-right text-sm">
                          {formatCurrency(row.metrics.shipped_list_wholesale)}
                        </td>
                      </>
                    )}
                    {hasVendorCost && (
                      <>
                        <td class="py-2 px-4 text-right text-sm">
                          {formatCurrency(row.metrics.ordered_vendor_cost)}
                        </td>
                        <td class="py-2 px-4 text-right text-sm">
                          {formatCurrency(row.metrics.shipped_vendor_cost)}
                        </td>
                      </>
                    )}
                    {!hasVendorCost && !hasListWholesale && !hasQuantity && (
                      <>
                        <td class="py-2 px-4 text-right">
                          {row.metrics.ordered_dollars 
                            ? formatCurrency(row.metrics.ordered_dollars)
                            : formatNumber(row.metrics.ordered)}
                        </td>
                        <td class="py-2 px-4 text-right">
                          {row.metrics.shipped_dollars 
                            ? formatCurrency(row.metrics.shipped_dollars)
                            : formatNumber(row.metrics.shipped)}
                        </td>
                      </>
                    )}
                  </>
                )}
                {/* Profitability cells — same toggle as header */}
                {hasProfitabilityData && show_deduction_data && (() => {
                  const p = getProfitability(row.name)
                  const deductions = p?.total_deductions ?? 0
                  const net = p?.net
                  const dedRate = p?.deduction_rate_pct
                  const marginPct = p?.margin_pct
                  return (
                    <>
                      <td class="py-2 px-4 text-right border-l border-gray-200 bg-orange-50/30 text-sm">
                        {p ? <span class="text-orange-700 font-medium">{formatCurrency(deductions)}</span> : <span class="text-gray-300">—</span>}
                      </td>
                      <td class="py-2 px-4 text-right bg-orange-50/30 text-sm">
                        {p ? (
                          <CogsCell
                            retailer={row.name}
                            selling_cost={p.selling_cost}
                            on_save={on_selling_cost_override_change}
                          />
                        ) : (
                          <span class="text-gray-300">—</span>
                        )}
                      </td>
                      <td class="py-2 px-4 text-right bg-orange-50/30 text-sm">
                        {net != null ? (
                          <span class={net >= 0 ? "text-green-700 font-medium" : "text-red-600 font-medium"}>
                            {formatCurrency(net)}
                          </span>
                        ) : <span class="text-gray-300">—</span>}
                      </td>
                      <td class="py-2 px-4 text-right bg-orange-50/30 text-sm">
                        {dedRate != null ? (
                          <span class={deduction_rate_color_class(dedRate)}>
                            {dedRate.toFixed(1)}%
                          </span>
                        ) : <span class="text-gray-300">—</span>}
                      </td>
                      <td class="py-2 px-4 text-right bg-orange-50/30 text-sm">
                        {marginPct != null ? (
                          <span class={`font-semibold ${margin_after_cogs_color_class(marginPct)}`}>
                            {marginPct.toFixed(1)}%
                          </span>
                        ) : <span class="text-gray-300">—</span>}
                      </td>
                    </>
                  )
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer with row count */}
      <div class="px-4 py-2 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
        Showing {rows.length} rows
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe("parse_dollar_input", () => {
    it("parses currency strings", () => {
      expect(parse_dollar_input("1234.56")).toBe(1234.56)
      expect(parse_dollar_input("$1,234")).toBe(1234)
      expect(parse_dollar_input("")).toBe(null)
    })
  })
  describe("margin_after_cogs_color_class", () => {
    it("uses lower thresholds than deduction rate", () => {
      expect(margin_after_cogs_color_class(5)).toContain("red")
      expect(margin_after_cogs_color_class(15)).toContain("yellow")
      expect(margin_after_cogs_color_class(25)).toContain("green")
    })
  })
  describe("format_deduction_invoice_window", () => {
    it("formats start and end range", () => {
      const r = {
        report_date: "2024-01-15",
        start_date: "2024-02-01",
        end_date: "2024-02-28",
      } as DistributorSalesReport
      expect(format_deduction_invoice_window(r)).toContain("Feb")
      expect(format_deduction_invoice_window(r)).toContain("2024")
    })
    it("uses calendar month of report_date when range missing", () => {
      const r = {
        report_date: "2024-03-10",
        start_date: null,
        end_date: null,
      } as unknown as DistributorSalesReport
      expect(format_deduction_invoice_window(r)).toMatch(/Mar 1, 2024.*Mar 31, 2024/)
    })
  })
}
